import React from 'react';
import PropTypes from 'prop-types';

/**
 * CustomLabel - A custom replacement for Semantic UI's Label component
 * Maintains the same API but uses custom styling
 */
const CustomLabel = React.forwardRef(({
  color,
  size = 'medium',
  icon,
  children,
  className = '',
  style = {},
  ...otherProps
}, ref) => {
  // Color mapping (matches Semantic UI colors)
  const getBackgroundColor = (color) => {
    const colorMap = {
      'red': '#db2828',
      'orange': '#f2711c',
      'yellow': '#fbbd08',
      'olive': '#b5cc18',
      'green': '#21ba45',
      'teal': '#00b5ad',
      'blue': '#2185d0',
      'violet': '#6435c9',
      'purple': '#a333c8',
      'pink': '#e03997',
      'brown': '#a5673f',
      'grey': '#767676',
      'black': '#1b1c1d'
    };
    return colorMap[color] || '#767676'; // Default to grey
  };

  // Size mapping
  const getFontSize = (size) => {
    const sizeMap = {
      'mini': '0.78571429rem',
      'tiny': '0.85714286rem',
      'small': '0.92857143rem',
      'medium': '1rem',
      'large': '1.14285714rem',
      'big': '1.28571429rem',
      'huge': '1.42857143rem',
      'massive': '1.71428571rem'
    };
    return sizeMap[size] || '1rem'; // Default to medium
  };

  // Determine text color based on background
  const getTextColor = (bgColor) => {
    // For yellow background, use darker text for better contrast
    if (bgColor === 'yellow' || bgColor === 'olive') {
      return 'rgba(0,0,0,.85)';
    }
    return '#FFFFFF';
  };

  return (
    <span
      ref={ref}
      className={`custom-label custom-label-${color} custom-label-${size} ${className}`}
      style={{
        display: 'inline-block',
        // padding: '0.5833rem 0.833rem',
        padding: '0.4833rem 0.633rem',

        borderRadius: '0.28571429rem',
        fontFamily: '"Lato", "Helvetica Neue", Arial, Helvetica, sans-serif',
        fontSize: getFontSize(size),
        fontWeight: '700',
        lineHeight: '1',
        verticalAlign: 'baseline',
        color: getTextColor(color),
        backgroundColor: getBackgroundColor(color),
        whiteSpace: 'nowrap',
        ...style
      }}
      {...otherProps}
    >
      {icon && (
        <img
          src={icon}
          alt=""
          style={{
            height: '16px',
            width: '16px',
            display: 'inline-block',
            verticalAlign: 'middle',
            marginRight: children ? '0.25em' : 0
          }}
        />
      )}
      {children}
    </span>
  );
});

CustomLabel.displayName = 'CustomLabel';

CustomLabel.propTypes = {
  color: PropTypes.string,
  size: PropTypes.oneOf(['mini', 'tiny', 'small', 'medium', 'large', 'big', 'huge', 'massive']),
  icon: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object
};

export default CustomLabel;